import { captureError, SENTRY_SEVERITY, toApiError } from 'src/utils/sentry'
import { JoinTerms, JoinTermsDetail, TelecomType, TermsDetail } from 'src/types/type'

const TELECOM_LABELS = ['SKTTerms', 'KTTerms', 'LGUPTerms'] as const

type MissingField = 'Title' | 'Contents'

function captureMissingTerm(
  message: string,
  tags: { telecom: string; termKey: string; missing: MissingField },
) {
  captureError(toApiError(new Error(message)), {
    level: SENTRY_SEVERITY.Error,
    tags,
    fingerprint: ['terms-missing', tags.telecom, tags.termKey, tags.missing],
  })
}

/**
 * 가입 약관 목록 검증 (§2.4-3 결정: 목록 단계에서는 Title 누락만 본다)
 *
 * mfinder 목록 응답(JoinTermsDetail)에는 Contents가 없고 Title만 있다.
 * Contents 누락은 상세 팝업을 열 때(captureTermsDetailError)만 검증한다.
 */
export function captureTermsListError(termsList: JoinTerms[]) {
  termsList.forEach((terms, telecomIndex) => {
    const telecom = TELECOM_LABELS[telecomIndex]
    if (!telecom || !terms?.Terms) return

    terms.Terms.forEach((term: JoinTermsDetail) => {
      if (term.Order === undefined) return // Terms.tsx에서 실제 렌더링되는 항목만

      const title = term.Title?.trim()
      if (!title) {
        captureMissingTerm(`[${telecom} : ${term.Order}번] Title 누락`, {
          telecom,
          termKey: String(term.Order),
          missing: 'Title',
        })
      }
    })
  })
}

/**
 * 약관 상세 팝업 검증 (§2.4-3 결정: Contents 누락은 여기서만 본다)
 *
 * Title 누락은 captureTermsListError에서 이미 캡처되므로 여기서는 중복 캡처하지 않는다.
 */
export function captureTermsDetailError(telecomType: TelecomType, idx: number, detail?: TermsDetail) {
  const telecom = TELECOM_LABELS[telecomType]
  if (!telecom || !detail) return

  const title = detail.TermsTitle?.trim()
  const contents = detail.TermsContents?.trim()

  if (!title) return
  if (!contents) {
    captureMissingTerm(`[${telecom} : ${title}] Contents 누락`, {
      telecom,
      termKey: String(idx),
      missing: 'Contents',
    })
  }
}

type MvnoTermsBody = {
  Type?: number
  TermsTitle?: string
  Terms?: string
}

/** KT MVNO 약관 검증 — Type===1(알뜰폰 해당)일 때만 */
export function captureMvnoTermsError(body?: MvnoTermsBody) {
  if (!body || body.Type !== 1) return

  const title = body.TermsTitle?.trim()
  const contents = body.Terms?.trim()

  if (!title) {
    captureMissingTerm('[KTTerms : MVNO] Title 누락', {
      telecom: 'KTTerms',
      termKey: 'MVNO',
      missing: 'Title',
    })
  }

  if (!contents) {
    captureMissingTerm('[KTTerms : MVNO] Contents 누락', {
      telecom: 'KTTerms',
      termKey: 'MVNO',
      missing: 'Contents',
    })
  }
}
